import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star } from 'lucide-react';
import { toast } from '@mlm/ui/components/toast';
import { cn } from '@mlm/ui/lib/cn';
import { AffirmationBlock } from '../components/panels/today';
import {
  CoachButton,
  ErrorNote,
  Eyebrow,
  inputClass,
  Loading,
  Screen,
} from '../components/primitives';
import { coaching, type Affirmation } from '../lib/api';
import { qk, useIsPro, useToday } from '../lib/queries';
import { ProLock } from '../components/pro-lock';

const SOURCE_LABEL: Record<Affirmation['source'], string> = {
  ai: 'Written by Master Kala',
  library: 'From the library',
  user: 'Yours',
};

/** SC-21: the member's affirmations, the ones used in the morning ritual, and new ones for the goal. */
export function AffirmationsPage() {
  const qc = useQueryClient();
  const today = useToday();
  const list = useQuery({ queryKey: qk.affirmations, queryFn: coaching.affirmations });
  const [text, setText] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: qk.affirmations });

  const add = useMutation({
    mutationFn: (value: string) => coaching.createAffirmation({ text: value }),
    meta: { successMessage: 'Affirmation saved.' },
    onSuccess: () => {
      setText('');
      void invalidate();
    },
  });
  const isPro = useIsPro();
  const generate = useMutation({
    mutationFn: () =>
      coaching.generateAffirmations({ goalId: today.data?.primaryGoal?.id, count: 3 }),
    onSuccess: (res) => {
      toast.success(
        res.affirmations.length ? 'New affirmations added to your list.' : 'Nothing new to add.',
      );
      void invalidate();
    },
  });
  // Favorite and "use in my morning ritual" flip at once; a failed save puts the old list back.
  const update = useMutation({
    mutationFn: (vars: { id: string; favorite?: boolean; inRitual?: boolean }) =>
      coaching.updateAffirmation(vars.id, { favorite: vars.favorite, inRitual: vars.inRitual }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: qk.affirmations });
      const previous = qc.getQueryData<Affirmation[]>(qk.affirmations);
      qc.setQueryData<Affirmation[]>(qk.affirmations, (current) =>
        current?.map((a) =>
          a.id === vars.id
            ? {
                ...a,
                favorite: vars.favorite ?? a.favorite,
                inRitual: vars.inRitual ?? a.inRitual,
              }
            : a,
        ),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) qc.setQueryData(qk.affirmations, context.previous);
    },
    onSettled: () => void invalidate(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => coaching.deleteAffirmation(id),
    meta: { successMessage: 'Affirmation removed.' },
    onSuccess: () => void invalidate(),
  });

  if (today.isPending || list.isPending) return <Loading />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;
  if (list.isError) return <ErrorNote error={list.error} retry={() => void list.refetch()} />;
  const items = list.data;
  const draft = text.trim();

  return (
    <Screen
      back="/manifest"
      eyebrow="Manifest"
      title="Affirmations"
      subtitle="Words to repeat until they feel true."
    >
      <AffirmationBlock today={today.data} />

      <section className="border-b border-border px-5 py-6">
        <form
          className="flex flex-col gap-3 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (draft.length >= 3 && !add.isPending) add.mutate(draft);
          }}
        >
          <label htmlFor="affirmation-text" className="sr-only">
            New affirmation
          </label>
          <input
            id="affirmation-text"
            className={inputClass}
            maxLength={220}
            value={text}
            placeholder="Write your own, in the present tense"
            onChange={(e) => setText(e.target.value)}
          />
          <CoachButton
            type="submit"
            className="shrink-0"
            disabled={draft.length < 3 || add.isPending}
          >
            Add
          </CoachButton>
        </form>
        {isPro ? (
          <CoachButton
            kind="quiet"
            className="mt-4"
            disabled={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? 'Writing' : 'Write three for my goal'}
          </CoachButton>
        ) : (
          <div className="mt-4">
            <ProLock feature="affirmation_generation" compact />
          </div>
        )}
      </section>

      <Eyebrow className="border-b border-border px-5 pb-3 pt-7">
        Your affirmations · {items.length}
      </Eyebrow>
      {items.length === 0 ? (
        <p className="px-5 py-5 text-sm leading-relaxed text-muted-foreground">
          No affirmations yet. Add your own, or let Master Kala write three for your goal.
        </p>
      ) : (
        <ul>
          {items.map((a) => (
            <li key={a.id} className="border-b border-border px-5 py-4">
              <div className="flex items-start gap-3">
                <p className="min-w-0 flex-1 font-display text-[1.15rem] italic leading-snug text-ink">
                  {a.text}
                </p>
                <button
                  type="button"
                  aria-pressed={a.favorite}
                  aria-label={a.favorite ? 'Remove from favorites' : 'Mark as favorite'}
                  onClick={() => update.mutate({ id: a.id, favorite: !a.favorite })}
                  className="-mr-3 -mt-2.5 flex h-11 w-11 shrink-0 items-center justify-center text-sage transition-colors hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-verde"
                >
                  <Star
                    className={cn('h-[18px] w-[18px]', a.favorite && 'fill-gold text-gold')}
                    strokeWidth={1.5}
                    aria-hidden
                  />
                </button>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-1">
                <span className="eyebrow">{SOURCE_LABEL[a.source]}</span>
                <label className="flex min-h-11 items-center gap-2 text-sm text-ink/80">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-verde"
                    checked={a.inRitual}
                    onChange={(e) => update.mutate({ id: a.id, inRitual: e.target.checked })}
                  />
                  Use in my morning ritual
                </label>
                <CoachButton
                  kind="quiet"
                  disabled={remove.isPending && remove.variables === a.id}
                  onClick={() => remove.mutate(a.id)}
                >
                  Remove
                </CoachButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Screen>
  );
}
